import type { AddCartItemInput, CartItem, CartOperation } from "../types/cart.types";

const isSameLine = (
    item: CartItem,
    identity: Pick<CartItem, "idProduct" | "idProductVariant">,
): boolean =>
    item.idProduct === identity.idProduct && item.idProductVariant === identity.idProductVariant;

const addItem = (items: CartItem[], item: AddCartItemInput): CartItem[] => {
    const quantity = item.quantity ?? 1;
    const existingItem = items.find((cartItem) => isSameLine(cartItem, item));

    if (!existingItem) {
        return [{ ...item, quantity, attributes: item.attributes || [] }, ...items];
    }

    const maxQuantity = item.stock ?? 999;
    const nextQuantity = Math.min(existingItem.quantity + quantity, maxQuantity);
    if (nextQuantity === existingItem.quantity) return items;

    return items.map((cartItem) =>
        isSameLine(cartItem, item) ? { ...cartItem, quantity: nextQuantity } : cartItem,
    );
};

const removeItem = (
    items: CartItem[],
    idProduct: string,
    idProductVariant?: string,
): CartItem[] => {
    const nextItems = items.filter(
        (item) => !(item.idProduct === idProduct && item.idProductVariant === idProductVariant),
    );

    return nextItems.length === items.length ? items : nextItems;
};

const updateItemQuantity = (
    items: CartItem[],
    idProduct: string,
    idProductVariant: string | undefined,
    quantity: number,
): CartItem[] => {
    let changed = false;
    const nextItems = items.map((item) => {
        if (item.idProduct !== idProduct || item.idProductVariant !== idProductVariant) {
            return item;
        }

        const maxQuantity = item.stock ?? 999;
        const safeQuantity = Math.min(Math.max(quantity, 1), maxQuantity);
        if (safeQuantity === item.quantity) return item;

        changed = true;
        return { ...item, quantity: safeQuantity };
    });

    return changed ? nextItems : items;
};

export const applyCartOperations = (
    currentItems: CartItem[],
    operations: CartOperation[],
): CartItem[] =>
    operations.reduce<CartItem[]>((items, operation) => {
        switch (operation.type) {
            case "add":
                return addItem(items, operation.item);
            case "remove":
                return removeItem(items, operation.idProduct, operation.idProductVariant);
            case "updateQuantity":
                return updateItemQuantity(
                    items,
                    operation.idProduct,
                    operation.idProductVariant,
                    operation.quantity,
                );
            case "clear":
                return items.length === 0 ? items : [];
            default:
                return items;
        }
    }, currentItems);
